import { useId, type ReactNode } from "react";
import { cn } from "../../utils";
import { Card, type CardProps } from "./card";

export interface DataTableFrameProps
  extends Omit<CardProps, "title" | "paddingMode"> {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  toolbar?: ReactNode;
  footer?: ReactNode;
  tableClassName?: string;
}

export function DataTableFrame({
  title,
  description,
  actions,
  toolbar,
  footer,
  tableClassName,
  className,
  children,
  elevation = "none",
  ...props
}: DataTableFrameProps) {
  const hasHeader = title || description || actions;
  const titleId = `${useId()}-title`;

  return (
    <Card
      aria-labelledby={title ? titleId : undefined}
      paddingMode="bleed"
      elevation={elevation}
      className={cn("overflow-hidden", className)}
      {...props}
    >
      {hasHeader ? (
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border-subtle px-surface-comfortable-x py-surface-compact-y">
          <div className="min-w-0">
            {title ? (
              <h2
                id={titleId}
                className="font-display text-heading-md font-semibold text-content-primary"
              >
                {title}
              </h2>
            ) : null}
            {description ? (
              <p className="mt-1 text-body text-content-muted">{description}</p>
            ) : null}
          </div>
          {actions ? (
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
      {toolbar ? (
        <div className="border-b border-border-subtle bg-surface-secondary/60 px-surface-comfortable-x py-surface-compact-y">
          {toolbar}
        </div>
      ) : null}
      <div
        className={cn("overflow-x-auto", tableClassName)}
        tabIndex={0}
        aria-labelledby={title ? titleId : undefined}
      >
        {children}
      </div>
      {footer ? (
        <div className="border-t border-border-subtle px-surface-comfortable-x py-surface-compact-y">
          {footer}
        </div>
      ) : null}
    </Card>
  );
}
